import type {
  BoardEvent,
  RouteConnectorPortName,
} from "../../../../../../../../../_logic/calendar-types"
import {
  ROUTE_GUTTER_WIDTH_PX,
  ROUTE_SOFT_COLUMN_WIDTH_PX,
} from "../../../_shared/_logic/route-constants"

const HARD_CARD_EDGE_INSET_PX = 10
const PARALLEL_CARD_EDGE_INSET_PX = 6
const SOFT_CARD_EDGE_INSET_PX = 6

export type RouteLayoutFrame = {
  totalWidthPx: number
  softWidthPx: number
  gutterWidthPx: number
  gutterCenterX: number
  hardLeftPx: number
  hardWidthPx: number
}

export type RoutePort = {
  name: RouteConnectorPortName
  x: number
  y: number
}

export type RoutePortSet = Record<RouteConnectorPortName, RoutePort>

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const displayTop = (item: BoardEvent) => (item.top ?? 0) + (item.routeInsetTopPx ?? 0)

const displayHeight = (item: BoardEvent) =>
  Math.max(1, (item.height ?? 1) - (item.routeInsetTopPx ?? 0) - (item.routeInsetBottomPx ?? 0))

export const routeLayoutFrameForWidth = (totalWidthPx: number): RouteLayoutFrame => {
  const safeTotalWidth = Math.max(1, totalWidthPx)
  const softWidthPx = Math.min(
    ROUTE_SOFT_COLUMN_WIDTH_PX,
    Math.max(0, safeTotalWidth - ROUTE_GUTTER_WIDTH_PX - 1),
  )
  const gutterWidthPx = Math.min(
    ROUTE_GUTTER_WIDTH_PX,
    Math.max(0, safeTotalWidth - softWidthPx - 1),
  )
  const hardLeftPx = softWidthPx

  return {
    totalWidthPx: safeTotalWidth,
    softWidthPx,
    gutterWidthPx,
    gutterCenterX: softWidthPx + gutterWidthPx / 2,
    hardLeftPx,
    hardWidthPx: Math.max(1, safeTotalWidth - hardLeftPx),
  }
}

export const routePortSetForEvent = (item: BoardEvent, frame: RouteLayoutFrame): RoutePortSet => {
  const top = displayTop(item)
  const bottom = top + displayHeight(item)
  const middleY = top + displayHeight(item) / 2
  const isSoft = item.event.flex_type === "SOFT"
  const laneLeftPx = isSoft
    ? 0
    : frame.hardLeftPx + ((item.laneLeftPct ?? 0) / 100) * frame.hardWidthPx
  const laneWidthPx = isSoft
    ? frame.softWidthPx
    : ((item.laneWidthPct ?? 100) / 100) * frame.hardWidthPx
  const cardInsetPx =
    (item.laneCount ?? 1) > 1 ? PARALLEL_CARD_EDGE_INSET_PX : HARD_CARD_EDGE_INSET_PX
  const leftX = isSoft
    ? clamp(SOFT_CARD_EDGE_INSET_PX, 0, frame.softWidthPx)
    : clamp(laneLeftPx + cardInsetPx, 0, frame.totalWidthPx)
  const rightX = isSoft
    ? clamp(frame.softWidthPx - SOFT_CARD_EDGE_INSET_PX, 0, frame.totalWidthPx)
    : clamp(laneLeftPx + laneWidthPx - cardInsetPx, 0, frame.totalWidthPx)
  const centerX = (leftX + rightX) / 2

  return {
    IN_TOP: { name: "IN_TOP", x: centerX, y: top },
    OUT_BOTTOM: { name: "OUT_BOTTOM", x: centerX, y: bottom },
    IN_LEFT: { name: "IN_LEFT", x: leftX, y: middleY },
    OUT_LEFT: { name: "OUT_LEFT", x: leftX, y: middleY },
    IN_RIGHT: { name: "IN_RIGHT", x: rightX, y: middleY },
    OUT_RIGHT: { name: "OUT_RIGHT", x: rightX, y: middleY },
  }
}

export const routePortCenterX = (item: BoardEvent, frame: RouteLayoutFrame) =>
  routePortSetForEvent(item, frame).IN_TOP.x
